import React from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import {
  AccessTime as HourlyIcon,
  Today as DailyIcon,
  DateRange as WeeklyIcon,
  CalendarMonth as MonthlyIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../context/CurrencyContext';

/**
 * Section Prix - Affichage simple des tarifs avec style accordéon
 */
const PriceSection = ({ 
  listing, 
  layout = 'comfortable',
  showIcon = true,
  isProduct = false
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();

  // Construire la liste des prix
  let prices = [];

  if (isProduct) {
    // Pour un produit : afficher le prix principal + variantes
    const basePrice = listing.price_daily;
    if (basePrice && basePrice > 0) {
      prices.push({
        period: 'product',
        value: basePrice,
        icon: null,
        label: t('search.product.basePrice', 'Prix'),
        shortLabel: t('search.product.basePrice', 'Prix'),
        priority: 1,
        isBase: true
      });
    }

    // Ajouter les variantes avec prix différents
    if (listing.productVariants && Array.isArray(listing.productVariants)) {
      listing.productVariants.forEach(variant => {
        if (variant.options && Array.isArray(variant.options)) {
          variant.options.forEach(option => {
            if (option.price_cents && option.price_cents > 0) {
              const variantPrice = option.price_cents / 100;
              prices.push({
                period: option.value,
                value: variantPrice,
                icon: null,
                label: `${variant.type}: ${option.value}`,
                shortLabel: option.value,
                priority: 2
              });
            }
          });
        }
      });
    }
  } else {
    // Pour un listing : afficher les périodes de prix
    prices = [
      {
        period: 'hourly',
        value: listing.price_hourly,
        icon: <HourlyIcon sx={{ fontSize: 14 }} />,
        label: t('listing.price.period.hourly.long'),
        shortLabel: t('listing.price.period.hourly.short'),
        priority: 1
      },
      {
        period: 'daily',
        value: listing.price_daily,
        icon: <DailyIcon sx={{ fontSize: 14 }} />,
        label: t('listing.price.period.daily.long'),
        shortLabel: t('listing.price.period.daily.short'),
        priority: 2
      },
      {
        period: 'weekly',
        value: listing.price_weekly,
        icon: <WeeklyIcon sx={{ fontSize: 14 }} />,
        label: t('listing.price.period.weekly.long'),
        shortLabel: t('listing.price.period.weekly.short'),
        priority: 3
      },
      {
        period: 'monthly',
        value: listing.price_monthly,
        icon: <MonthlyIcon sx={{ fontSize: 14 }} />,
        label: t('listing.price.period.monthly.long'),
        shortLabel: t('listing.price.period.monthly.short'),
        priority: 4
      }
    ];
  }

  prices = prices.filter(price => price.value && price.value > 0);

  // Si aucun prix n'est disponible
  if (prices.length === 0) {
    return (
      <Box sx={{ 
        mx: 0.5,
        p: 1, 
        backgroundColor: alpha(theme.palette.grey[500], 0.05),
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`
      }}>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ fontStyle: 'italic', textAlign: 'center', fontSize: '0.75rem' }}
        >
          {t('listing.price.contact')}
        </Typography>
      </Box>
    );
  }

  // Déterminer le prix principal à afficher
  const primaryPrice = prices[0];

  // Formatage de la devise
  const formatCurrency = (amount) => {
    return formatPrice(amount, listing.currency || 'EUR');
  };

  if (layout === 'compact') {
    return (
      <Box sx={{ 
        mx: 0.5,
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        {/* Affichage de tous les prix alignés */}
        <Box sx={{ p: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {prices.map((price, index) => (
              <Box 
                key={price.period}
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.4,
                  px: 0.6,
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  {showIcon && (
                    <Box sx={{ 
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {price.icon}
                    </Box>
                  )}
                  <Typography 
                    variant="caption" 
                    color="primary.main"
                    fontWeight={500}
                    sx={{ fontSize: '0.65rem' }}
                  >
                    {t(`listing.price.period.${price.period}.short`, price.shortLabel)}
                  </Typography>
                </Box>
                
                <Typography 
                  variant="caption" 
                  fontWeight={600}
                  color="primary.main"
                  sx={{ fontSize: '0.7rem' }}
                >
                  {formatCurrency(price.value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // Pour les autres layouts
  return (
    <Box sx={{ 
      mx: 1,
      mb: 2,
      backgroundColor: alpha(theme.palette.primary.main, 0.03),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
      borderRadius: 2,
      overflow: 'hidden'
    }}>
      {/* Affichage de tous les prix alignés */}
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {prices.map((price, index) => (
            <Box 
              key={price.period}
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                px: 1.2,
                borderRadius: 1.5,
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {showIcon && (
                  <Box sx={{ 
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {price.icon}
                  </Box>
                )}
                <Typography 
                  variant="body2" 
                  fontWeight={500}
                  color="primary.main"
                >
                  {t(`listing.price.period.${price.period}.long`)}
                </Typography>
              </Box>
              
              <Typography 
                variant="body2" 
                fontWeight={600}
                color="primary.main"
              >
                {formatCurrency(price.value)}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Affichage de la caution si disponible */}
        {listing.deposit_amount && listing.deposit_amount > 0 && (
          <Box sx={{ 
            mt: 1.5, 
            pt: 1, 
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: alpha(theme.palette.warning.main, 0.05),
            borderRadius: 1,
            p: 1
          }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              💰 {t('listing.deposit.required')}
            </Typography>
            <Typography variant="body2" fontWeight={600} color="warning.main">
              {formatCurrency(listing.deposit_amount)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PriceSection;
